import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailablePerformanceStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportPerformanceLocation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchPerformanceReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setPerformanceStationReportUpdate,
  setSortData,
  refreshPerformanceReportStation,
} from '../../../../redux/slices/ReportPerformanceLocationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * Reports management page
 * @param {any} properties - The properties
 */
function PerformanceReportTableStation(properties: Readonly<ReportTableProperties>) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isPerformanceStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportPerformanceLocation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  /**
   * Fetches Performance report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchPerformanceReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPerformanceReportStation({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const {
    isEditMode,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleOpenStationSidePanel,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: reports,
    disableEdit: true,
    disableDelete: true,
  });
  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailablePerformanceStationReport,
      /**
       * S.No view
       * @param {any} report - the performance station report data
       * @param {number} index - index of the row
       */
      customData: (report: AvailablePerformanceStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailablePerformanceStationReport,
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Station info view
       * @param {any} report - the performance station report data
       */
      customData: (report: AvailablePerformanceStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenStationSidePanel(report);
              }}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailablePerformanceStationReport,
      sortAllow: true,
    },
    {
      label: t('freeUsage'),
      accessorKey: 'freeSessionCount' as keyof AvailablePerformanceStationReport,
      sortAllow: true,
    },
    {
      label: t('paidUsage'),
      accessorKey: 'paidSessionCount' as keyof AvailablePerformanceStationReport,
      sortAllow: true,
    },
  ];

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Current columns for Performance Station Report export
   */
  const currentColumns = [
    { label: 'SNo', accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('freeUsage'), accessorKey: 'freeSessionCount' },
    { label: t('paidUsage'), accessorKey: 'paidSessionCount' },
    { label: 'Created', accessorKey: 'createdAt' },
    { label: 'Last Updated', accessorKey: 'updatedAt' },
  ];
  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchPerformanceStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailablePerformanceStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'freeSessionCount':
                return row?.freeSessionCount ?? '0';

              case 'paidSessionCount':
                return row?.paidSessionCount ?? '0';

              case 'createdAt':
                return (row as any).createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any).updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters for fetching the report
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    });
  };

  /**
   * Auto Refresh the table
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshPerformanceReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        accountCustomId: reportsData?.accountId,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };
  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isPerformanceStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setPerformanceStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isPerformanceStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'performanceReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={370}
          created
          lastUpdated
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          sizeChangeButtonRequired
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          rightTopContent={rightTopContent}
        />
      </div>
    </div>
  );
}

export default PerformanceReportTableStation;
